import { UniButton } from '@/design-system/components/buttons/UniButton';
import { UniCheckbox } from '@/design-system/components/inputs';
import { H6, TextBody, TextTitle } from '@/design-system/components/typography/UniTypography';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { H2, SafeAreaView, ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'react-native';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import CheckCircleSvg from '../../../../assets/svg/Check Circle Outline.svg';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { useNavigatorPage } from '../../hooks/useNavigatorPage.hook';
import ExchangeImageBackground from '../background/exchange-image-background';
import GraphicComponent from './Graphic.component';

export interface ExchangeIntroductionProps {}
const MAX_WIDTH_INFO_VIEW = 620;
const MAX_WIDTH_BUTTON_VIEW = 450;
export const ExchangeIntroductionDesktop: React.FC<ExchangeIntroductionProps> = () => {
  const layout = useContext(LayoutContext);
  const [selected, setSelected] = useState<boolean>(false);
  const store = useStore();
  const { t } = useTranslation('exchange-sub-account');
  const { width } = useWindowDimensions();
  const navigator = useNavigatorPage();
  const isFullDesktop = layout.screenSize === ScreenSize.DesktopFull;

  const onSelectChange = (value: boolean) => {
    setSelected(value);
  };

  const onBtnOpenAccountClick = () => {
    if (!!selected) {
      setSelected(false);
      return navigator.pushScreen(RootStackRoutes.ExchangeSubAccount, ExchangeSubAccountStackRoutes.SelectExchange);
    }
  };

  return (
    <View sx={sxStyles.container}>
      <ExchangeImageBackground />
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.mainView}>
          <SafeAreaView
            sx={{
              ...sxStyles.infoView,
              ...{
                width: isFullDesktop ? MAX_WIDTH_INFO_VIEW : undefined,
                flex: isFullDesktop ? undefined : 1,
                mr: isFullDesktop ? '$25' : 0,
                pl: isFullDesktop ? undefined : '$4'
              }
            }}>
            <View sx={sxStyles.bodyInfoView}>
              <View sx={sxStyles.titleView}>
                <H2 bold sx={sxStyles.primaryColor}>
                  {t('exchange-account::landing-page.text--discover-possibilities')}
                </H2>
              </View>
              <View sx={sxStyles.introTextView}>
                <H6 sx={sxStyles.introText}>{t('exchange-account::landing-page.text--get-connected')}</H6>
              </View>
              <View sx={sxStyles.listText}>
                <View sx={sxStyles.listTextItem}>
                  <TextTitle sx={sxStyles.textItem}>{t('exchange-account::landing-page.text--why-utgl')}</TextTitle>
                </View>
                <View sx={sxStyles.listTextItem}>
                  <View sx={sxStyles.checkView}>
                    <CheckCircleSvg width={30} />
                  </View>
                  <View sx={sxStyles.textItemView}>
                    <TextTitle semiBold sx={sxStyles.textItem}>
                      {t('exchange-account::landing-page.text--lower-trading-fees')}
                    </TextTitle>
                  </View>
                </View>
                <View sx={sxStyles.listTextItem}>
                  <View sx={sxStyles.checkView}>
                    <CheckCircleSvg width={30} />
                  </View>
                  <View sx={sxStyles.textItemView}>
                    <TextTitle semiBold sx={sxStyles.textItem}>
                      {t('exchange-account::landing-page.text--access-and-enables')}
                    </TextTitle>
                  </View>
                </View>
                <View sx={sxStyles.listTextItem}>
                  <View sx={sxStyles.checkView}>
                    <CheckCircleSvg width={30} />
                  </View>
                  <View sx={sxStyles.textItemView}>
                    <TextTitle semiBold sx={sxStyles.textItem}>
                      {t('exchange-account::landing-page.text--margin-leverage')}
                    </TextTitle>
                  </View>
                </View>
                <View sx={sxStyles.listTextItem}>
                  <View sx={sxStyles.checkView}>
                    <CheckCircleSvg width={30} />
                  </View>
                  <View sx={sxStyles.textItemView}>
                    <TextTitle semiBold sx={sxStyles.textItem}>
                      {t('exchange-account::landing-page.text--view-and-manage')}
                    </TextTitle>
                  </View>
                </View>
              </View>
              <View sx={sxStyles.confirmTextView}>
                <UniCheckbox
                  sx={sxStyles.checkBox}
                  value={selected}
                  onValueChange={(value) => onSelectChange(value)}
                  verticalAlignCenter></UniCheckbox>
                <TextBody sx={sxStyles.confirmText}>
                  {t('exchange-account::landing-page.text--application-notice', { vendor: store.vendor ?? 'UTGL' })}
                </TextBody>
              </View>
              <View
                sx={{ ...sxStyles.btnOpenAccountView, ...{ width: width / 2 > MAX_WIDTH_BUTTON_VIEW ? MAX_WIDTH_BUTTON_VIEW : '100%' } }}>
                <UniButton
                  disabled={!selected}
                  label={t('exchange-account::landing-page.button--get-started')}
                  onPress={() => onBtnOpenAccountClick()}
                  sx={sxStyles.buttonOpenAccount}
                  size="medium"
                />
              </View>
            </View>
          </SafeAreaView>
          <SafeAreaView
            sx={{ ...sxStyles.graphicView, ...{ width: isFullDesktop ? undefined : undefined, flex: isFullDesktop ? undefined : 1 } }}>
            <View sx={sxStyles.bodyGraphicView}>
              <View sx={sxStyles.imageView}>
                <GraphicComponent />
              </View>
            </View>
          </SafeAreaView>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    alignItems: 'center'
  },
  checkView: {
    width: 30,
    marginTop: -5
  },
  introTextView: {
    mb: '$7',
    width: '95%'
  },
  introText: {
    color: '$textDarkBackground',
    fontWeight: 'bold'
  },
  mainView: {
    flexDirection: 'row',
    height: '100%',
    py: '$4'
  },
  infoView: {
    mr: '25',
    width: '620px',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  graphicView: {
    justifyContent: 'center',
    flexDirection: 'column'
  },
  buttonOpenAccount: {
    height: 56
  },
  primaryColor: {
    color: '#E3B354'
  },
  titleText: {
    color: '$textTitle'
  },
  listTextItem: {
    mb: '$4',
    flexDirection: 'row'
  },
  titleView: {
    mb: '$5'
  },
  textItemView: {
    pl: '$2',
    flexDirection: 'row',
    width: '70%'
  },
  textItem: {
    color: '$textDarkBackground'
  },
  listText: {
    mb: '$1'
  },
  confirmTextView: {
    width: '98%',
    mb: '$10',
    flexDirection: 'row'
  },
  confirmText: {
    color: '$textDarkBackground'
  },
  btnOpenAccountView: {
    width: '450px',
    borderRadius: '$radius1',
    height: '56px'
  },
  checkBox: {
    alignSelf: 'flex-start',
    mr: '$2'
  }
};
